<template>
    <div class="JobCard h-full overflow-hidden rounded-lg bg-white dark:bg-surface-card shadow hover:shadow-lg transition-shadow">
        <div class="flex h-full flex-col">
            <!-- Organization and job image -->
            <div class="relative h-48 w-full bg-gray-200 dark:bg-subtle">
                <img
                    v-if="job.coverImg || job.image"
                    :src="job.coverImg || job.image"
                    :alt="job.title || 'Pracovná ponuka'"
                    class="size-full object-cover"
                    loading="lazy"
                />
                <div v-else class="flex h-full items-center justify-center">
                    <img
                        v-if="job.orgImage || job.orgMeta?.image"
                        :src="job.orgImage || job.orgMeta?.image"
                        :alt="orgTitle"
                        class="max-h-20 max-w-[80%] object-contain"
                        loading="lazy"
                    />
                    <span v-else class="text-lg text-gray-400 dark:text-txt-body">{{ orgTitle || 'Pracovná ponuka' }}</span>
                </div>
            </div>

            <!-- Job content -->
            <div class="flex flex-1 flex-col p-5">
                <!-- Job title -->
                <h3 class="mb-2 text-lg font-semibold dark:text-white">{{ job.title || 'Pracovná ponuka' }}</h3>

                <!-- Organization name -->
                <div v-if="orgTitle" class="mb-4 flex items-center">
                    <img
                        v-if="job.orgImage || job.orgMeta?.image"
                        :src="job.orgImage || job.orgMeta?.image"
                        :alt="orgTitle"
                        class="mr-2 size-6 rounded-full object-contain"
                        loading="lazy"
                    />
                    <span class="text-sm text-gray-600 dark:text-txt-body">{{ orgTitle }}</span>
                </div>

                <!-- Job details -->
                <div class="mb-4 space-y-2 text-sm text-gray-700 dark:text-txt-body">

                    <div v-if="job.remote" class="flex items-center">
                        <CIcon name="globe" class="mr-2 size-4 shrink-0 text-gray-400 dark:text-txt-body" />
                        <span>{{ remoteWorkText }}</span>
                    </div>

                    <div v-if="locationText" class="flex items-center">
                        <CIcon name="location-outline" class="mr-2 size-4 shrink-0 text-gray-400 dark:text-txt-body" />
                        <span>{{ locationText }}</span>
                    </div>

                    <div v-if="salaryText" class="flex items-center">
                        <CIcon name="winnings" class="mr-2 size-4 shrink-0 text-gray-400 dark:text-txt-body" />
                        <span>{{ salaryText }}</span>
                    </div>
                </div>

                <!-- Categories/Tags -->
                <div v-if="displayCategories.length" class="mb-3 flex flex-wrap gap-1">
                    <span
                        v-for="category in displayCategories.slice(0, 3)"
                        :key="category"
                        class="inline-block bg-gray-100 dark:bg-subtle text-gray-700 dark:text-txt-body text-xs px-2 py-1 rounded"
                    >
                        {{ category }}
                    </span>
                    <span v-if="displayCategories.length > 3" class="text-xs text-gray-500 dark:text-txt-body">
                        +{{ displayCategories.length - 3 }} ďalšie
                    </span>
                </div>

                <!-- CTA button -->
                <div class="mt-auto pt-3">
                    <NuxtLink
                        :to="{ name: 'praca-slug', params: { slug: job.slug || job.id || job.objectID } }"
                        class="inline-flex items-center font-medium text-blue-600 dark:text-blue-300 hover:text-blue-800 hover:dark:text-blue-300"
                    >
                        Zobraziť detail →
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { JobListItem } from '@/types/search'

const props = defineProps<{
    job: JobListItem
}>()

const orgTitle = computed(() => {
    return props.job.orgName || props.job.orgMeta?.title || ''
})

const locationText = computed(() => {
    if (props.job.location) {
        return props.job.location
    }
    if (props.job.jobLocation && Array.isArray(props.job.jobLocation)) {
        return props.job.jobLocation.join(', ')
    }
    return ''
})

const remoteWorkText = computed(() => {
    if (props.job.remote) {
        return 'Možnosť práce na diaľku'
    }
    return 'Na mieste'
})

const salaryText = computed(() => {
    const salaryFrom = props.job.salaryMin
    const salaryTo = props.job.salaryMax
    
    if (salaryFrom && salaryTo) {
        return `${salaryFrom} - ${salaryTo} €`
    } else if (salaryFrom) {
        return `Od ${salaryFrom} €`
    } else if (salaryTo) {
        return `Do ${salaryTo} €`
    }
    return ''
})

const displayCategories = computed(() => {
    if (props.job.topics && Array.isArray(props.job.topics)) {
        return props.job.topics
    }
    return []
})
</script>
